/**
 * @file ScaledPreviewFrame.tsx
 * @description Рамка образца баннера реальной ширины, уменьшенная под
 * колонку предпросмотра (phase19.16, Р3г; повторный дефект QA D2: образец
 * «360» в колонке 328 px и «ПК» в колонке 256 px обрезали кнопки).
 *
 * Образец раскладывается в своей настоящей ширине (360 — телефон, 728 или
 * 1024 — широкий экран; контейнерные запросы видят именно её), затем
 * уменьшается `transform: scale(колонка / ширина)` от левого верхнего угла,
 * а высота рамки — по масштабу (ResizeObserver на колонке и образце). Ничего
 * не сжимается и не прокручивается: видно то, что увидит посетитель, только
 * мельче. Шире колонки не растягиваем (масштаб ≤ 1), образец по центру.
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { previewScale } from "../../../lib/platform/consent-preview";

interface Props {
  /** Настоящая ширина образца, px. */
  width: number;
  /** Подпись ширины для тестов и QA: `narrow` / `wide`. */
  widthKey: string;
  children: ReactNode;
}

export function ScaledPreviewFrame({ width, widthKey, children }: Props) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ container: 0, height: 0 });

  useLayoutEffect(() => {
    const measure = () => {
      setBox({ container: outer.current?.clientWidth ?? 0, height: inner.current?.offsetHeight ?? 0 });
    };
    measure();
    // В тестовом окружении (jsdom) наблюдателя размеров нет — хватит первого замера.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    if (outer.current) observer.observe(outer.current);
    if (inner.current) observer.observe(inner.current);
    return () => observer.disconnect();
  }, [width]);

  const scale = previewScale(box.container, width);
  const left = box.container > width * scale ? (box.container - width * scale) / 2 : 0;

  return (
    <div
      ref={outer}
      data-preview-scale={scale.toFixed(3)}
      className="relative w-full overflow-hidden"
      style={{ height: box.height > 0 ? Math.ceil(box.height * scale) : undefined }}
    >
      <div
        ref={inner}
        aria-live="off"
        aria-label="Образец"
        role="group"
        data-preview-width={widthKey}
        className="@container flex min-h-24 flex-col justify-end"
        style={{
          width,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          // Пока высоты нет (первый кадр, jsdom) — образец в потоке, иначе рамка нулевой высоты.
          position: box.height > 0 ? "absolute" : "relative",
          left,
          top: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
}
